import React, { useState, useMemo, useRef } from 'react';
import { observer } from 'mobx-react';
import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  Modifier,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import { IconButton, Popover, InputAdornment } from '@mui/material';

import AddEntityModal from 'components/Entities/AddEntityModal/AddEntityModal';

import { useStore } from 'utils/hooks/store';

import { UserRole } from 'core/constants';

import { ADMIN_AND_MANAGERS } from 'utils/roles/roleGroups';

import { FlattenedTreeItem, FolderType } from 'stores/Folders/types';

import { StyledTextField } from 'components/Input/styles';

import FolderTreeItem from './FolderTreeItem/FolderTreeItem';
import { FolderDragContext } from './FolderTreeItem/DragContext';
import CreateFolderModal from './Modals/CreateFolderModal';
import ImportDocumentModal from './Modals/ImportDocumentModal';
import AcknowledgementsPin from './AcknowledgementsPin';
import { MenuItem } from './FolderTreeItem/styles';

import {
  Container,
  Title,
  TitleContainer,
  TitleRow,
  EmptySearchContainer,
  EmptySearchTitle,
  EmptySearchDescription,
  EmptySearchHelper,
} from './styles';

const filterFolders = (folders: FolderType[], query: string): FolderType[] => {
  const lowerQuery = query.toLowerCase();

  const filterFolder = (folder: FolderType): FolderType | null => {
    const folderNameMatches = folder.name.toLowerCase().includes(lowerQuery);
    const matchingDocuments = folder.documents?.filter((doc) => doc.name.toLowerCase().includes(lowerQuery)) || [];
    const filteredSubfolders = folder.subfolders.map(filterFolder).filter((f): f is FolderType => f !== null);

    if (folderNameMatches || matchingDocuments.length > 0 || filteredSubfolders.length > 0) {
      return {
        ...folder,
        documents: folderNameMatches ? folder.documents : matchingDocuments,
        subfolders: filteredSubfolders,
      };
    }

    return null;
  };

  return folders.map(filterFolder).filter((f): f is FolderType => f !== null);
};

// Measured from the DOM because dnd-kit exposes no droppable rects to a modifier. Reads the
// sortable node itself — the element the transform is applied to — before any transform lands.
const measureGroupBounds = (uuids: string[]): { top: number; bottom: number } | null => {
  const first = document.querySelector(`[data-row-uuid="${uuids[0]}"]`);
  const last = document.querySelector(`[data-row-uuid="${uuids[uuids.length - 1]}"]`);
  if (!first || !last) return null;

  return { top: first.getBoundingClientRect().top, bottom: last.getBoundingClientRect().bottom };
};

const FolderNavigation: React.FC = () => {
  const { folders: foldersStore, session, documentsDetails, notification } = useStore();
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isCreateDocumentModalOpen, setIsCreateDocumentModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeRow, setActiveRow] = useState<FlattenedTreeItem | null>(null);
  const groupBoundsRef = useRef<{ top: number; bottom: number } | null>(null);

  const canImport = Boolean(session?.user?.tiptapConvertToken);
  const hasDocumentsLicense = Boolean(session?.user?.licenses?.documents);
  const canManageFolders = hasDocumentsLicense && ADMIN_AND_MANAGERS.includes(session?.user?.role as UserRole);

  const isMenuOpen = Boolean(menuAnchorEl);

  const filteredFolders = useMemo(() => {
    if (!searchQuery.trim()) {
      return foldersStore.folders;
    }
    return filterFolders(foldersStore.folders, searchQuery);
  }, [foldersStore.folders, searchQuery]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(event.target.value);
  };

  const handleClearSearch = (): void => {
    setSearchQuery('');
  };

  const hasSearchQuery = searchQuery.trim().length > 0;
  const showEmptyState = hasSearchQuery && filteredFolders.length === 0;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setMenuAnchorEl(null);
  };

  const handleNewFolder = (): void => {
    handleMenuClose();
    setIsCreateFolderModalOpen(true);
  };

  const handleNewDocument = (): void => {
    handleMenuClose();
    setIsCreateDocumentModalOpen(true);
  };

  const handleImportDocument = (): void => {
    handleMenuClose();
    // The backend rejects documents created with no folder, so the import
    // dialog's required Destination field can't be populated without one.
    if (foldersStore.folders.length === 0) {
      notification.enqueueErrorSnackbar('Create a folder first, then try importing.');
      return;
    }
    setIsImportModalOpen(true);
  };

  const handleCloseFolderModal = (): void => {
    setIsCreateFolderModalOpen(false);
  };

  const handleCloseDocumentModal = (): void => {
    setIsCreateDocumentModalOpen(false);
  };

  const handleCloseImportModal = (): void => {
    setIsImportModalOpen(false);
  };

  const handleCreateFolder = (name: string, parent: string | null): void => {
    foldersStore.createFolder(name, parent);
  };

  // Search renders a filtered projection of the tree, so reordering it is meaningless.
  const canReorder = canManageFolders && !hasSearchQuery;
  const flattenedTree = foldersStore.flattenedTree;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const detectSiblingCollision: CollisionDetection = (args) => {
    const dragged = foldersStore.treeRow(String(args.active.id));
    if (!dragged) return closestCenter(args);

    const siblings = new Set(foldersStore.siblingsOf(dragged).map((row) => row.uuid));

    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter((container) => siblings.has(String(container.id))),
    });
  };

  const restrictToSiblingGroup: Modifier = ({ transform, draggingNodeRect }) => {
    const bounds = groupBoundsRef.current;
    if (!bounds || !draggingNodeRect) return transform;

    return {
      ...transform,
      y: Math.min(Math.max(transform.y, bounds.top - draggingNodeRect.top), bounds.bottom - draggingNodeRect.bottom),
    };
  };

  const handleDragStart = ({ active }: DragStartEvent): void => {
    const dragged = foldersStore.treeRow(String(active.id)) ?? null;
    setActiveRow(dragged);
    groupBoundsRef.current = dragged
      ? measureGroupBounds(foldersStore.siblingsOf(dragged).map((row) => row.uuid))
      : null;
  };

  const handleDragCancel = (): void => {
    setActiveRow(null);
    groupBoundsRef.current = null;
  };

  const handleDragEnd = ({ active, over }: DragEndEvent): void => {
    setActiveRow(null);
    groupBoundsRef.current = null;

    if (!over || active.id === over.id) return;

    const dragged = foldersStore.treeRow(String(active.id));
    const target = foldersStore.treeRow(String(over.id));
    if (!dragged || !target) return;

    // Defensive: collision detection already confines `over` to the sibling group.
    if (!foldersStore.areSiblings(dragged, target)) return;

    foldersStore.moveWithinSiblings(dragged, target);
  };

  return (
    <Container>
      <TitleContainer>
        <TitleRow>
          <Title>Documents</Title>
          {hasDocumentsLicense && (
            <IconButton onClick={handleMenuOpen} size="small">
              <AddCircleIcon htmlColor="#03A9F4" />
            </IconButton>
          )}
          <Popover
            open={isMenuOpen}
            anchorEl={menuAnchorEl}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {canManageFolders && <MenuItem onClick={handleNewFolder}>New Folder</MenuItem>}
            <MenuItem onClick={handleNewDocument}>New Document</MenuItem>
            {canImport && <MenuItem onClick={handleImportDocument}>Import Document</MenuItem>}
          </Popover>
        </TitleRow>
        <AcknowledgementsPin />
        <StyledTextField
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search..."
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
              </InputAdornment>
            ),
            endAdornment: hasSearchQuery ? (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} size="small" edge="end">
                  <CloseIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 20 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          style={{
            backgroundColor: 'transparent',
          }}
          sx={{
            '& .MuiInputBase-root fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.12) !important',
            },
            '& .MuiInputBase-input': {
              color: 'rgb(255, 255, 255) !important',
              '&::placeholder': {
                color: 'rgba(255, 255, 255, 0.5) !important',
              },
            },
          }}
        />
      </TitleContainer>
      {showEmptyState ? (
        <EmptySearchContainer>
          <FindInPageOutlinedIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 64 }} />
          <EmptySearchTitle>No results found</EmptySearchTitle>
          <EmptySearchDescription>No documents or folders match &quot;{searchQuery}&quot;</EmptySearchDescription>
          <EmptySearchHelper>Try a different search or browse the folder tree.</EmptySearchHelper>
        </EmptySearchContainer>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={detectSiblingCollision}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          modifiers={[restrictToVerticalAxis, restrictToSiblingGroup]}
        >
          <SortableContext items={flattenedTree.map((row) => row.uuid)} strategy={verticalListSortingStrategy}>
            <FolderDragContext.Provider value={{ canReorder, activeRow }}>
              {filteredFolders.map((folder) => (
                <FolderTreeItem key={folder.uuid} folder={folder} depth={0} />
              ))}
            </FolderDragContext.Provider>
          </SortableContext>
        </DndContext>
      )}
      <CreateFolderModal
        open={isCreateFolderModalOpen}
        onClose={handleCloseFolderModal}
        onCreateFolder={handleCreateFolder}
        folders={foldersStore.folders}
      />
      {isCreateDocumentModalOpen && <AddEntityModal onClose={handleCloseDocumentModal} store={documentsDetails} />}
      {isImportModalOpen && <ImportDocumentModal onClose={handleCloseImportModal} />}
    </Container>
  );
};

export default observer(FolderNavigation);
